import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db, auth } from '../firebase';
import {
    doc,
    getDoc,
    updateDoc,
    collection,
    query,
    where,
    getDocs,
    setDoc,
    writeBatch,
    serverTimestamp
} from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { nanoid } from 'nanoid';
import {
    FaArrowLeft,
    FaEnvelope,
    FaLock,
    FaBan,
    FaCheckCircle,
    FaPhone,
    FaQrcode,
    FaCar,
    FaSave,
    FaTrashAlt,
    FaExternalLinkAlt,
    FaPlus,
    FaShieldAlt,
    FaSpinner,
    FaExclamationTriangle,
    FaEye,
    FaEyeSlash,
    FaInfoCircle,
    FaCalendarAlt
} from 'react-icons/fa';

const AdminUserDetailPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingUser, setSavingUser] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [notification, setNotification] = useState({ type: '', message: '' });

    // User Edit Form State
    const [displayName, setDisplayName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [role, setRole] = useState('customer');
    const [isPhoneVerified, setIsPhoneVerified] = useState(false);
    const [isEmailVerified, setIsEmailVerified] = useState(false);

    // Linked QRs & Vehicle Profiles
    const [linkedQrs, setLinkedQrs] = useState([]);
    const [loadingQrs, setLoadingQrs] = useState(true);
    const [editingQrId, setEditingQrId] = useState(null);
    const [qrFormState, setQrFormState] = useState({});

    // New QR Linking State
    const [newQrInput, setNewQrInput] = useState('');
    const [generatingQr, setGeneratingQr] = useState(false);
    const [unlinkedQrsList, setUnlinkedQrsList] = useState([]);

    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification({ type: '', message: '' }), 5000);
    };

    // Fetch User Data
    const fetchUserData = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const userDocRef = doc(db, 'users', userId);
            const userSnap = await getDoc(userDocRef);

            if (userSnap.exists()) {
                const data = { id: userSnap.id, ...userSnap.data() };
                setUser(data);
                setDisplayName(data.displayName || '');
                setPhoneNumber(data.phoneNumber || '');
                setRole(data.role || 'customer');
                setIsPhoneVerified(!!data.isPhoneVerified);
                setIsEmailVerified(!!data.isEmailVerified);
            } else {
                showNotification('error', 'Kullanıcı bulunamadı.');
            }
        } catch (err) {
            console.error('Kullanıcı bilgisi alınırken hata:', err);
            showNotification('error', 'Kullanıcı bilgisi yüklenemedi.');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    // Fetch Linked QR codes and vehicle profiles
    const fetchUserQrs = useCallback(async () => {
        if (!userId) return;
        setLoadingQrs(true);
        try {
            // Query qrCodes linked to this user
            const q = query(collection(db, 'qrCodes'), where('linkedUserId', '==', userId));
            const qrSnap = await getDocs(q);
            const qrList = qrSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            // Also query vehicleProfiles for this user
            const vpQuery = query(collection(db, 'vehicleProfiles'), where('userId', '==', userId));
            const vpSnap = await getDocs(vpQuery);
            const vpMap = {};
            vpSnap.docs.forEach(d => {
                vpMap[d.id] = d.data();
            });

            // Merge QR and Profile data
            const merged = qrList.map(qr => {
                const profile = vpMap[qr.id] || {};
                return {
                    ...qr,
                    profile: {
                        vehiclePlate: profile.vehiclePlate || '',
                        visibleNote: profile.visibleNote || '',
                        contactInfo: profile.contactInfo || {
                            phone: { value: '', isVisible: true },
                            email: { value: '', isVisible: true },
                            telegram: { value: '', isVisible: true },
                            website: { value: '', isVisible: true },
                        },
                        internalData: profile.internalData || {
                            muayeneGecerlilikTarihi: '',
                            sigortaExpireDate: '',
                            kaskoExpireDate: ''
                        }
                    }
                };
            });

            setLinkedQrs(merged);

            // Fetch a few unlinked QRs for the dropdown helper
            const unlinkedQuery = query(collection(db, 'qrCodes'), where('isLinked', '==', false));
            const unlinkedSnap = await getDocs(unlinkedQuery);
            const unlinkedList = unlinkedSnap.docs.slice(0, 50).map(d => d.id);
            setUnlinkedQrsList(unlinkedList);

        } catch (err) {
            console.error('Kullanıcı QR kodları alınırken hata:', err);
        } finally {
            setLoadingQrs(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchUserData();
        fetchUserQrs();
    }, [fetchUserData, fetchUserQrs]);

    // Save basic user info
    const handleSaveUser = async (e) => {
        e.preventDefault();
        setSavingUser(true);
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                displayName,
                phoneNumber,
                role,
                isPhoneVerified,
                isEmailVerified,
                updatedAt: serverTimestamp()
            });
            showNotification('success', 'Kullanıcı bilgileri başarıyla güncellendi.');
            fetchUserData();
        } catch (err) {
            console.error('Kullanıcı güncelleme hatası:', err);
            showNotification('error', `Güncelleme başarısız: ${err.message}`);
        } finally {
            setSavingUser(false);
        }
    };

    // Send Password Reset Email
    const handleSendPasswordReset = async () => {
        if (!user?.email) {
            return showNotification('error', 'Kullanıcının kayıtlı bir e-posta adresi bulunmuyor.');
        }

        if (!window.confirm(`${user.email} adresine şifre sıfırlama bağlantısı gönderilsin mi?`)) {
            return;
        }

        setActionLoading(true);
        try {
            await sendPasswordResetEmail(auth, user.email);
            showNotification('success', `Şifre sıfırlama e-postası başarıyla gönderildi: ${user.email}`);
        } catch (err) {
            console.error('Şifre sıfırlama hatası:', err);
            showNotification('error', `Şifre sıfırlama gönderilemedi: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    // Toggle Account Suspension (Dondur / Aktifleştir)
    const handleToggleSuspension = async () => {
        const isCurrentlySuspended = !!user?.isSuspended;
        const confirmText = isCurrentlySuspended
            ? 'Bu kullanıcının hesabını tekrar aktifleştirmek istediğinizden emin misiniz?'
            : 'Bu kullanıcının hesabını DONDURMAK (askıya almak) istediğinizden emin misiniz?';

        if (!window.confirm(confirmText)) return;

        setActionLoading(true);
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                isSuspended: !isCurrentlySuspended,
                status: isCurrentlySuspended ? 'active' : 'suspended',
                suspendedAt: isCurrentlySuspended ? null : serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            showNotification(
                'success',
                isCurrentlySuspended
                    ? 'Hesap başarıyla aktifleştirildi.'
                    : 'Hesap başarıyla donduruldu (askıya alındı).'
            );
            fetchUserData();
        } catch (err) {
            console.error('Hesap dondurma hatası:', err);
            showNotification('error', `İşlem başarısız: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    // Toggle Verification Quick Switchers
    const handleTogglePhoneVerified = async () => {
        const nextVal = !isPhoneVerified;
        setIsPhoneVerified(nextVal);
        try {
            await updateDoc(doc(db, 'users', userId), {
                isPhoneVerified: nextVal,
                updatedAt: serverTimestamp()
            });
            showNotification('success', `Telefon doğrulanma durumu güncellendi: ${nextVal ? 'Doğrulandı' : 'Doğrulanmadı'}`);
        } catch (err) {
            setIsPhoneVerified(!nextVal);
            showNotification('error', 'Durum güncellenemedi.');
        }
    };

    const handleToggleEmailVerified = async () => {
        const nextVal = !isEmailVerified;
        setIsEmailVerified(nextVal);
        try {
            await updateDoc(doc(db, 'users', userId), {
                isEmailVerified: nextVal,
                updatedAt: serverTimestamp()
            });
            showNotification('success', `E-posta doğrulanma durumu güncellendi: ${nextVal ? 'Doğrulandı' : 'Doğrulanmadı'}`);
        } catch (err) {
            setIsEmailVerified(!nextVal);
            showNotification('error', 'Durum güncellenemedi.');
        }
    };

    // Start editing a specific QR's profile
    const startEditingQr = (qrItem) => {
        setEditingQrId(qrItem.id);
        const p = qrItem.profile || {};
        const ci = p.contactInfo || {};
        const idata = p.internalData || {};

        setQrFormState({
            vehiclePlate: p.vehiclePlate || '',
            visibleNote: p.visibleNote || '',
            phoneVal: ci.phone?.value ?? ci.phone ?? '',
            phoneVis: ci.phone?.isVisible ?? true,
            emailVal: ci.email?.value ?? ci.email ?? '',
            emailVis: ci.email?.isVisible ?? true,
            telegramVal: ci.telegram?.value ?? ci.telegram ?? '',
            telegramVis: ci.telegram?.isVisible ?? true,
            websiteVal: ci.website?.value ?? ci.website ?? '',
            websiteVis: ci.website?.isVisible ?? true,
            muayeneDate: idata.muayeneGecerlilikTarihi || '',
            sigortaDate: idata.sigortaExpireDate || '',
            kaskoDate: idata.kaskoExpireDate || '',
        });
    };

    // Save QR and Profile Settings
    const handleSaveQrSettings = async (qrId) => {
        try {
            const profileRef = doc(db, 'vehicleProfiles', qrId);
            await setDoc(profileRef, {
                profileId: qrId,
                userId: userId,
                qrCodeId: qrId,
                vehiclePlate: qrFormState.vehiclePlate || '',
                visibleNote: qrFormState.visibleNote || '',
                contactInfo: {
                    phone: { value: qrFormState.phoneVal || '', isVisible: !!qrFormState.phoneVis },
                    email: { value: qrFormState.emailVal || '', isVisible: !!qrFormState.emailVis },
                    telegram: { value: qrFormState.telegramVal || '', isVisible: !!qrFormState.telegramVis },
                    website: { value: qrFormState.websiteVal || '', isVisible: !!qrFormState.websiteVis },
                },
                internalData: {
                    muayeneGecerlilikTarihi: qrFormState.muayeneDate || '',
                    sigortaExpireDate: qrFormState.sigortaDate || '',
                    kaskoExpireDate: qrFormState.kaskoDate || '',
                },
                updatedAt: serverTimestamp()
            }, { merge: true });

            showNotification('success', `${qrId} QR ayarları başarıyla kaydedildi.`);
            setEditingQrId(null);
            fetchUserQrs();
        } catch (err) {
            console.error('QR ayarları kaydedilirken hata:', err);
            showNotification('error', `Kayıt başarısız: ${err.message}`);
        }
    };

    // Unlink QR from user (Free it up)
    const handleUnlinkQr = async (qrId) => {
        if (!window.confirm(`${qrId} kodlu QR'ın bu kullanıcıyla olan bağlantısını koparmak istediğinizden emin misiniz? QR boşa çıkacaktır.`)) {
            return;
        }

        try {
            const batch = writeBatch(db);
            const qrRef = doc(db, 'qrCodes', qrId);
            const profileRef = doc(db, 'vehicleProfiles', qrId);

            batch.update(qrRef, {
                isLinked: false,
                linkedUserId: null,
                unlinkedAt: serverTimestamp()
            });

            batch.delete(profileRef);

            await batch.commit();
            showNotification('success', `${qrId} QR bağlantısı başarıyla kaldırıldı ve QR boşa çıkarıldı.`);
            fetchUserQrs();
        } catch (err) {
            console.error('QR bağlantısı kaldırılırken hata:', err);
            showNotification('error', `İşlem başarısız: ${err.message}`);
        }
    };

    // Link an existing unlinked QR to this user
    const handleLinkExistingQr = async (codeToLink) => {
        const cleanCode = (codeToLink || newQrInput).trim().toUpperCase();
        if (!cleanCode) return showNotification('error', 'Lütfen geçerli bir QR kodu girin.');

        try {
            const qrRef = doc(db, 'qrCodes', cleanCode);
            const qrSnap = await getDoc(qrRef);

            if (!qrSnap.exists()) {
                return showNotification('error', `"${cleanCode}" kodlu QR veritabanında bulunamadı.`);
            }

            const qrData = qrSnap.data();
            if (qrData.isLinked) {
                return showNotification('error', `"${cleanCode}" kodu zaten başka bir kullanıcıya (${qrData.linkedUserId}) bağlı.`);
            }

            const batch = writeBatch(db);
            batch.update(qrRef, {
                isLinked: true,
                linkedUserId: userId,
                linkedAt: serverTimestamp()
            });

            const profileRef = doc(db, 'vehicleProfiles', cleanCode);
            batch.set(profileRef, {
                profileId: cleanCode,
                userId: userId,
                qrCodeId: cleanCode,
                vehiclePlate: '',
                visibleNote: 'Kısa süre içinde döneceğim.',
                showContactInfoGlobally: true,
                contactInfo: {
                    phone: { value: phoneNumber || '', isVisible: true },
                    email: { value: user?.email || '', isVisible: true },
                    telegram: { value: '', isVisible: true },
                    website: { value: '', isVisible: true }
                },
                internalData: {
                    muayeneGecerlilikTarihi: '',
                    sigortaExpireDate: '',
                    kaskoExpireDate: ''
                },
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            await batch.commit();
            showNotification('success', `"${cleanCode}" kodu başarıyla bu kullanıcıya bağlandı!`);
            setNewQrInput('');
            fetchUserQrs();
        } catch (err) {
            console.error('QR bağlama hatası:', err);
            showNotification('error', `Bağlama başarısız: ${err.message}`);
        }
    };

    // Generate brand new QR and directly link to this user
    const handleGenerateAndLinkNewQr = async () => {
        setGeneratingQr(true);
        try {
            const prefix = role === 'company' ? 'RC' : 'KT';
            const newCode = `${prefix}-${nanoid(6).toUpperCase()}`;

            const batch = writeBatch(db);
            const qrRef = doc(db, 'qrCodes', newCode);
            batch.set(qrRef, {
                code: newCode,
                isLinked: true,
                linkedUserId: userId,
                createdAt: serverTimestamp(),
                linkedAt: serverTimestamp(),
                prefix: prefix
            });

            const profileRef = doc(db, 'vehicleProfiles', newCode);
            batch.set(profileRef, {
                profileId: newCode,
                userId: userId,
                qrCodeId: newCode,
                vehiclePlate: '',
                visibleNote: 'Kısa süre içinde döneceğim.',
                showContactInfoGlobally: true,
                contactInfo: {
                    phone: { value: phoneNumber || '', isVisible: true },
                    email: { value: user?.email || '', isVisible: true },
                    telegram: { value: '', isVisible: true },
                    website: { value: '', isVisible: true }
                },
                internalData: {
                    muayeneGecerlilikTarihi: '',
                    sigortaExpireDate: '',
                    kaskoExpireDate: ''
                },
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            await batch.commit();
            showNotification('success', `Yeni "${newCode}" QR kodu üretildi ve doğrudan bu kullanıcıya bağlandı!`);
            fetchUserQrs();
        } catch (err) {
            console.error('Yeni QR oluşturulurken hata:', err);
            showNotification('error', `Yeni QR oluşturulamadı: ${err.message}`);
        } finally {
            setGeneratingQr(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Kullanıcı detayları yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="container mx-auto px-4 py-12 max-w-4xl text-center">
                <FaExclamationTriangle className="text-5xl text-amber-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Kullanıcı Bulunamadı</h2>
                <p className="text-gray-600 mb-6">İstenilen kullanıcı mevcut değil veya silinmiş olabilir.</p>
                <Link to="/admin/users" className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
                    Tüm Kullanıcılara Dön
                </Link>
            </div>
        );
    }

    const isSuspended = !!user.isSuspended;

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto space-y-6">

                {/* Back Link & Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate('/admin/users')}
                            className="p-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-black transition-colors"
                            title="Tüm Kullanıcılara Dön"
                        >
                            <FaArrowLeft size={16} />
                        </button>
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white font-bold text-2xl flex items-center justify-center shadow-md">
                            {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="text-2xl font-bold text-gray-900">{user.displayName || 'İsimsiz Kullanıcı'}</h1>
                                {isSuspended ? (
                                    <span className="px-2.5 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full flex items-center gap-1">
                                        <FaBan size={10} /> Donduruldu / Askıda
                                    </span>
                                ) : (
                                    <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full flex items-center gap-1">
                                        <FaCheckCircle size={10} /> Aktif
                                    </span>
                                )}
                                <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                                    user.role === 'company'
                                        ? 'bg-purple-100 text-purple-700'
                                        : user.role === 'admin'
                                        ? 'bg-rose-100 text-rose-700'
                                        : 'bg-blue-100 text-blue-700'
                                }`}>
                                    {user.role === 'company' ? 'Rent A Car' : user.role === 'admin' ? 'Yönetici' : 'Bireysel'}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 font-mono mt-1">UID: {user.id}</p>
                        </div>
                    </div>

                    {/* Top Action Quick Buttons */}
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Send Password Reset Email */}
                        <button
                            type="button"
                            onClick={handleSendPasswordReset}
                            disabled={actionLoading}
                            className="px-4 py-2.5 bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200 rounded-xl text-xs font-semibold flex items-center gap-2 transition-all shadow-sm"
                            title="Kullanıcının kayıtlı e-postasına şifre sıfırlama linki gönder"
                        >
                            <FaLock size={12} />
                            <span>Şifre Sıfırlama Gönder</span>
                        </button>

                        {/* Freeze / Unfreeze Account */}
                        <button
                            type="button"
                            onClick={handleToggleSuspension}
                            disabled={actionLoading}
                            className={`px-4 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-2 transition-all shadow-sm ${
                                isSuspended
                                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                    : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                            }`}
                        >
                            <FaBan size={12} />
                            <span>{isSuspended ? 'Hesabı Aktifleştir' : 'Hesabı Dondur'}</span>
                        </button>
                    </div>
                </div>

                {/* Notifications Alert */}
                {notification.message && (
                    <div className={`p-4 rounded-xl text-sm font-medium flex items-center gap-2 ${
                        notification.type === 'error'
                            ? 'bg-red-50 text-red-800 border border-red-200'
                            : 'bg-green-50 text-green-800 border border-green-200'
                    }`}>
                        {notification.type === 'error' ? <FaExclamationTriangle /> : <FaCheckCircle />}
                        <span>{notification.message}</span>
                    </div>
                )}

                {/* Two-Column Grid: Left: User Information Form | Right: Quick Status Toggles */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                    {/* Column 1 & 2: User Edit Form */}
                    <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center justify-between pb-4 mb-4 border-b">
                            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <FaShieldAlt className="text-blue-600" />
                                <span>Kullanıcı Bilgileri & Düzenleme</span>
                            </h2>
                            <span className="text-xs text-gray-500">
                                Kayıt: {user.createdAt?.seconds ? new Date(user.createdAt.seconds * 1000).toLocaleDateString('tr-TR') : 'Bilinmiyor'}
                            </span>
                        </div>

                        <form onSubmit={handleSaveUser} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">Ad Soyad / Firma Adı</label>
                                    <input
                                        type="text"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">E-Posta (Kayıtlı)</label>
                                    <input
                                        type="email"
                                        value={user.email || ''}
                                        disabled
                                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 text-sm cursor-not-allowed"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">Telefon Numarası</label>
                                    <input
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        placeholder="05XXXXXXXXX"
                                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">Sistem Rolü</label>
                                    <select
                                        value={role}
                                        onChange={(e) => setRole(e.target.value)}
                                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                                    >
                                        <option value="customer">Bireysel Araç Sahibi (customer)</option>
                                        <option value="company">Rent A Car / Filo Şirketi (company)</option>
                                        <option value="admin">Sistem Yöneticisi (admin)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Additional Company Info if applicable */}
                            {user.role === 'company' && (
                                <div className="mt-4 p-4 bg-purple-50 rounded-xl border border-purple-100 text-xs space-y-2">
                                    <p className="font-bold text-purple-900">Kurumsal Bilgiler</p>
                                    <p><span className="text-gray-500">Mevcut Araç Slotu:</span> <span className="font-bold">{user.carSlots || 0}</span></p>
                                    <p><span className="text-gray-500">Şirket Profil ID:</span> <span className="font-mono">{user.companyProfileId || 'Yok'}</span></p>
                                    <Link
                                        to={`/admin/edit-company/${user.id}`}
                                        className="inline-block mt-2 font-semibold text-purple-700 hover:text-purple-900 underline"
                                    >
                                        Şirket Detay & Slot Yönetim Sayfasına Git →
                                    </Link>
                                </div>
                            )}

                            <div className="pt-4 flex justify-end">
                                <button
                                    type="submit"
                                    disabled={savingUser}
                                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center gap-2 shadow-sm transition-all disabled:opacity-50"
                                >
                                    {savingUser ? <FaSpinner className="animate-spin" /> : <FaSave />}
                                    <span>Değişiklikleri Kaydet</span>
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Column 3: Verification Quick Toggles */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 pb-4 border-b">
                            <FaCheckCircle className="text-emerald-600" />
                            <span>Doğrulama & Güvenlik</span>
                        </h2>

                        {/* Phone Verification Toggle */}
                        <div className="p-4 rounded-xl border bg-gray-50 flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-1.5 font-bold text-sm text-gray-800">
                                    <FaPhone className="text-blue-500" size={13} />
                                    <span>Telefon Onayı</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    {isPhoneVerified ? 'Kullanıcı telefonu onaylı' : 'Doğrulama bekleniyor'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleTogglePhoneVerified}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                    isPhoneVerified
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                {isPhoneVerified ? 'Onaylı' : 'Onaysız (Aç)'}
                            </button>
                        </div>

                        {/* Email Verification Toggle */}
                        <div className="p-4 rounded-xl border bg-gray-50 flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-1.5 font-bold text-sm text-gray-800">
                                    <FaEnvelope className="text-indigo-500" size={13} />
                                    <span>E-Posta Onayı</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    {isEmailVerified ? 'E-posta adresi onaylı' : 'Doğrulama bekleniyor'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleToggleEmailVerified}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                    isEmailVerified
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                {isEmailVerified ? 'Onaylı' : 'Onaysız (Aç)'}
                            </button>
                        </div>

                        {/* Account Status Box */}
                        <div className="p-4 rounded-xl border border-dashed border-gray-300 text-xs text-gray-600 space-y-1">
                            <p className="font-semibold text-gray-800 flex items-center gap-1">
                                <FaInfoCircle /> Yönetici Notu
                            </p>
                            <p>Kullanıcının doğrulama adımlarında takılması durumunda yukarıdaki düğmelerle manuel onay verilebilir.</p>
                        </div>
                    </div>
                </div>

                {/* Section: Linked QR Codes & Vehicle Profiles */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <FaQrcode className="text-rose-600" />
                                <span>Kullanıcıya Bağlı QR Kodlar & Araç Profilleri ({linkedQrs.length})</span>
                            </h2>
                            <p className="text-xs text-gray-500 mt-1">
                                Bu kullanıcıya tanımlı QR kodlarını inceleyebilir, iletişim ve plaka ayarlarını güncelleyebilir veya bağlantısını koparabilirsiniz.
                            </p>
                        </div>

                        {/* Fast Add/Generate QR Button */}
                        <button
                            type="button"
                            onClick={handleGenerateAndLinkNewQr}
                            disabled={generatingQr}
                            className="px-4 py-2.5 bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white rounded-xl text-xs font-semibold flex items-center gap-2 shadow-sm transition-all"
                        >
                            {generatingQr ? <FaSpinner className="animate-spin" /> : <FaPlus />}
                            <span>Yeni QR Üret & Bu Kullanıcıya Ata</span>
                        </button>
                    </div>

                    {/* Manual Link Unlinked QR Section */}
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 flex flex-col md:flex-row items-center gap-3">
                        <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                            Var Olan Boşta Bir QR Bağla:
                        </span>
                        <input
                            type="text"
                            value={newQrInput}
                            onChange={(e) => setNewQrInput(e.target.value)}
                            placeholder="Örn: KT123ABC veya RC-XYZ..."
                            className="flex-1 px-3.5 py-2 rounded-xl border border-gray-300 text-xs font-mono uppercase focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        {unlinkedQrsList.length > 0 && (
                            <select
                                onChange={(e) => setNewQrInput(e.target.value)}
                                className="px-3 py-2 rounded-xl border border-gray-300 text-xs bg-white focus:outline-none"
                                value=""
                            >
                                <option value="" disabled>Boştaki Kodlardan Seç</option>
                                {unlinkedQrsList.map(code => (
                                    <option key={code} value={code}>{code}</option>
                                ))}
                            </select>
                        )}
                        <button
                            type="button"
                            onClick={() => handleLinkExistingQr()}
                            className="px-4 py-2 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-semibold whitespace-nowrap transition-colors"
                        >
                            QR Kodunu Bağla
                        </button>
                    </div>

                    {/* QR Codes List */}
                    {loadingQrs ? (
                        <div className="py-12 text-center text-gray-500">
                            <FaSpinner className="animate-spin text-2xl mx-auto mb-2 text-rose-500" />
                            <p className="text-xs">Bağlı QR kodlar yükleniyor...</p>
                        </div>
                    ) : linkedQrs.length === 0 ? (
                        <div className="py-12 text-center border-2 border-dashed border-gray-200 rounded-xl">
                            <FaQrcode className="text-4xl text-gray-300 mx-auto mb-3" />
                            <h3 className="font-semibold text-gray-700 mb-1">Henüz Bağlı QR Kod Yok</h3>
                            <p className="text-xs text-gray-500 mb-4">Bu kullanıcıya henüz herhangi bir QR sticker bağlanmamış.</p>
                            <button
                                type="button"
                                onClick={handleGenerateAndLinkNewQr}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700"
                            >
                                İlk QR Kodunu Oluştur ve Ata
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {linkedQrs.map((item) => {
                                const isEditing = editingQrId === item.id;
                                const p = item.profile || {};
                                const ci = p.contactInfo || {};
                                const idata = p.internalData || {};

                                return (
                                    <div key={item.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm transition-all hover:border-gray-300">
                                        {/* Card Header */}
                                        <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-black text-white flex items-center justify-center font-mono font-bold text-sm">
                                                    QR
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-mono font-bold text-base text-gray-900">{item.id}</span>
                                                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full">
                                                            Bağlı
                                                        </span>
                                                        <a
                                                            href={`https://kodutara.web.app/q/${item.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-gray-400 hover:text-blue-600 text-xs flex items-center gap-1"
                                                            title="QR Taramasını Test Et (Yeni Sekme)"
                                                        >
                                                            <FaExternalLinkAlt size={11} />
                                                        </a>
                                                    </div>
                                                    <p className="text-xs text-gray-500">
                                                        Plaka: <span className="font-bold text-gray-700">{p.vehiclePlate || 'Tanımlanmamış'}</span> • Not: {p.visibleNote || 'Yok'}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Actions on this QR */}
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => isEditing ? setEditingQrId(null) : startEditingQr(item)}
                                                    className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 text-xs font-semibold text-gray-700 transition-colors"
                                                >
                                                    {isEditing ? 'Kapat' : 'Ayarları Düzenle'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleUnlinkQr(item.id)}
                                                    className="px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-xs font-semibold text-red-700 transition-colors flex items-center gap-1"
                                                    title="QR Bağlantısını Kopar (Boşa çıkarır)"
                                                >
                                                    <FaTrashAlt size={11} />
                                                    <span>Bağlantıyı Kaldır</span>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Editing Form Expanded */}
                                        {isEditing ? (
                                            <div className="p-6 bg-white space-y-5 border-t border-gray-100">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {/* Plate */}
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                                                            <FaCar className="text-gray-500" /> Araç Plakası
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={qrFormState.vehiclePlate}
                                                            onChange={(e) => setQrFormState(prev => ({ ...prev, vehiclePlate: e.target.value.toUpperCase() }))}
                                                            placeholder="34 ABC 123"
                                                            className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm font-mono uppercase focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                        />
                                                    </div>

                                                    {/* Visible Note */}
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                            Kullanıcı Notu (QR okutulunca görünen mesaj)
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={qrFormState.visibleNote}
                                                            onChange={(e) => setQrFormState(prev => ({ ...prev, visibleNote: e.target.value }))}
                                                            placeholder="Örn: 10 dk sonra geleceğim..."
                                                            className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Contact Details & Visibility Settings */}
                                                <div>
                                                    <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2">
                                                        İletişim Seçenekleri & Görünürlük (Gizlilik)
                                                    </h4>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        {/* Phone */}
                                                        <div className="p-3 border rounded-xl bg-gray-50 flex items-center justify-between gap-2">
                                                            <div className="flex-1">
                                                                <span className="text-xs font-semibold text-gray-700">Telefon:</span>
                                                                <input
                                                                    type="text"
                                                                    value={qrFormState.phoneVal}
                                                                    onChange={(e) => setQrFormState(prev => ({ ...prev, phoneVal: e.target.value }))}
                                                                    className="w-full mt-1 px-2.5 py-1 text-xs border rounded bg-white"
                                                                    placeholder="Telefon"
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => setQrFormState(prev => ({ ...prev, phoneVis: !prev.phoneVis }))}
                                                                className={`p-2 rounded-lg text-xs font-bold flex items-center gap-1 ${
                                                                    qrFormState.phoneVis ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                                                                }`}
                                                                title="Görünürlük Durumu"
                                                            >
                                                                {qrFormState.phoneVis ? <FaEye /> : <FaEyeSlash />}
                                                            </button>
                                                        </div>

                                                        {/* Email */}
                                                        <div className="p-3 border rounded-xl bg-gray-50 flex items-center justify-between gap-2">
                                                            <div className="flex-1">
                                                                <span className="text-xs font-semibold text-gray-700">E-Posta:</span>
                                                                <input
                                                                    type="text"
                                                                    value={qrFormState.emailVal}
                                                                    onChange={(e) => setQrFormState(prev => ({ ...prev, emailVal: e.target.value }))}
                                                                    className="w-full mt-1 px-2.5 py-1 text-xs border rounded bg-white"
                                                                    placeholder="E-Posta"
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => setQrFormState(prev => ({ ...prev, emailVis: !prev.emailVis }))}
                                                                className={`p-2 rounded-lg text-xs font-bold flex items-center gap-1 ${
                                                                    qrFormState.emailVis ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                                                                }`}
                                                                title="Görünürlük Durumu"
                                                            >
                                                                {qrFormState.emailVis ? <FaEye /> : <FaEyeSlash />}
                                                            </button>
                                                        </div>

                                                        {/* Telegram */}
                                                        <div className="p-3 border rounded-xl bg-gray-50 flex items-center justify-between gap-2">
                                                            <div className="flex-1">
                                                                <span className="text-xs font-semibold text-gray-700">Telegram:</span>
                                                                <input
                                                                    type="text"
                                                                    value={qrFormState.telegramVal}
                                                                    onChange={(e) => setQrFormState(prev => ({ ...prev, telegramVal: e.target.value }))}
                                                                    className="w-full mt-1 px-2.5 py-1 text-xs border rounded bg-white"
                                                                    placeholder="@kullaniciadi"
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => setQrFormState(prev => ({ ...prev, telegramVis: !prev.telegramVis }))}
                                                                className={`p-2 rounded-lg text-xs font-bold flex items-center gap-1 ${
                                                                    qrFormState.telegramVis ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                                                                }`}
                                                                title="Görünürlük Durumu"
                                                            >
                                                                {qrFormState.telegramVis ? <FaEye /> : <FaEyeSlash />}
                                                            </button>
                                                        </div>

                                                        {/* Website */}
                                                        <div className="p-3 border rounded-xl bg-gray-50 flex items-center justify-between gap-2">
                                                            <div className="flex-1">
                                                                <span className="text-xs font-semibold text-gray-700">Web Sitesi:</span>
                                                                <input
                                                                    type="text"
                                                                    value={qrFormState.websiteVal}
                                                                    onChange={(e) => setQrFormState(prev => ({ ...prev, websiteVal: e.target.value }))}
                                                                    className="w-full mt-1 px-2.5 py-1 text-xs border rounded bg-white"
                                                                    placeholder="https://..."
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => setQrFormState(prev => ({ ...prev, websiteVis: !prev.websiteVis }))}
                                                                className={`p-2 rounded-lg text-xs font-bold flex items-center gap-1 ${
                                                                    qrFormState.websiteVis ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                                                                }`}
                                                                title="Görünürlük Durumu"
                                                            >
                                                                {qrFormState.websiteVis ? <FaEye /> : <FaEyeSlash />}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Inspection / Muayene & Sigorta Dates */}
                                                <div>
                                                    <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2 flex items-center gap-1">
                                                        <FaCalendarAlt className="text-amber-500" /> Muayene, Sigorta ve Kasko Tarihleri
                                                    </h4>
                                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                        <div>
                                                            <label className="block text-[11px] text-gray-600 mb-1">Muayene Bitiş Tarihi</label>
                                                            <input
                                                                type="date"
                                                                value={qrFormState.muayeneDate}
                                                                onChange={(e) => setQrFormState(prev => ({ ...prev, muayeneDate: e.target.value }))}
                                                                className="w-full px-2.5 py-1.5 text-xs border rounded-lg"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-[11px] text-gray-600 mb-1">Sigorta Bitiş Tarihi</label>
                                                            <input
                                                                type="date"
                                                                value={qrFormState.sigortaDate}
                                                                onChange={(e) => setQrFormState(prev => ({ ...prev, sigortaDate: e.target.value }))}
                                                                className="w-full px-2.5 py-1.5 text-xs border rounded-lg"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-[11px] text-gray-600 mb-1">Kasko Bitiş Tarihi</label>
                                                            <input
                                                                type="date"
                                                                value={qrFormState.kaskoDate}
                                                                onChange={(e) => setQrFormState(prev => ({ ...prev, kaskoDate: e.target.value }))}
                                                                className="w-full px-2.5 py-1.5 text-xs border rounded-lg"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Form Actions */}
                                                <div className="flex justify-end gap-2 pt-2 border-t">
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditingQrId(null)}
                                                        className="px-4 py-2 border rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-50"
                                                    >
                                                        İptal
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSaveQrSettings(item.id)}
                                                        className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm"
                                                    >
                                                        <FaSave size={12} />
                                                        <span>QR Ayarlarını Kaydet</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Summary View */
                                            <div className="px-5 py-3 text-xs text-gray-600 flex flex-wrap gap-4 items-center bg-white">
                                                <div>
                                                    <span className="text-gray-400">Telefon:</span>{' '}
                                                    <span className="font-medium">{ci.phone?.value || '—'}</span>{' '}
                                                    <span className={ci.phone?.isVisible ? 'text-green-600' : 'text-gray-400'}>
                                                        ({ci.phone?.isVisible ? 'Görünür' : 'Gizli'})
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-400">Muayene:</span>{' '}
                                                    <span className="font-medium text-gray-800">{idata.muayeneGecerlilikTarihi || 'Belirtilmedi'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-400">Sigorta:</span>{' '}
                                                    <span className="font-medium text-gray-800">{idata.sigortaExpireDate || 'Belirtilmedi'}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default AdminUserDetailPage;
